<template>
  <v-container fluid class="bg-light-soft min-h-screen pa-6">
    <!-- Header Section -->
    <v-row no-gutters class="mb-8 align-center">
      <v-col cols="12" md="8">
        <div class="d-flex align-center">
          <v-btn @click="$router.back()" icon class="mr-4 bg-white shadow-sm rounded-lg" color="primary">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div>
            <div class="d-flex align-center mb-1">
              <v-icon color="primary" class="mr-2" small>mdi-file-tree</v-icon>
              <span class="text-overline grey--text font-weight-black letter-spacing-1">Gestión Contable</span>
            </div>
            <h1 class="text-h4 font-weight-black grey--text text--darken-4">
              Catálogo de <span class="primary--text">Cuentas</span>
            </h1>
          </div>
        </div>
      </v-col>
      <v-col cols="12" md="4" class="text-right">
        <div class="d-flex justify-end gap-2">
          <v-menu bottom left offset-y transition="scale-transition">
            <template v-slot:activator="{ on, attrs }">
              <v-btn color="white" depressed class="rounded-xl px-6 font-weight-bold shadow-sm border-soft"
                v-bind="attrs" v-on="on">
                <v-icon left color="success">mdi-download</v-icon> Exportar
              </v-btn>
            </template>
            <v-list class="py-0 rounded-lg overflow-hidden">
              <v-list-item @click="downloadPDF" class="hover-bg-light">
                <v-list-item-icon class="mr-3"><v-icon color="error">mdi-file-pdf-box</v-icon></v-list-item-icon>
                <v-list-item-title class="font-weight-bold">Documento PDF</v-list-item-title>
              </v-list-item>
              <v-divider></v-divider>
              <v-list-item @click="downloadXLSX" class="hover-bg-light">
                <v-list-item-icon class="mr-3"><v-icon color="success">mdi-file-excel-box</v-icon></v-list-item-icon>
                <v-list-item-title class="font-weight-bold">Hoja de Excel</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn v-if="$store.state.auth.globalFeatures.includes('CON-012')" color="primary" depressed
            class="rounded-xl px-6 font-weight-bold" @click="addRootAccount">
            <v-icon left>mdi-plus</v-icon> Nueva Cuenta
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <!-- Toolbar & Filters -->
    <v-card class="rounded-xl border-soft shadow-sm mb-6 overflow-hidden" elevation="0">
      <v-toolbar flat class="px-4 bg-white" height="72">
        <!-- View Toggle -->
        <v-btn-toggle v-model="viewMode" mandatory dense borderless class="bg-grey-soft rounded-lg pa-1">
          <v-btn value="tree" small class="rounded-lg px-4 font-weight-bold"
            active-class="white primary--text shadow-xs">
            <v-icon left small>mdi-file-tree</v-icon> Árbol
          </v-btn>
          <v-btn value="table" small class="rounded-lg px-4 font-weight-bold"
            active-class="white primary--text shadow-xs">
            <v-icon left small>mdi-table</v-icon> Tabla
          </v-btn>
        </v-btn-toggle>

        <v-divider vertical class="mx-6 my-4 opacity-10"></v-divider>

        <!-- Search -->
        <v-text-field v-model="search" prepend-inner-icon="mdi-magnify"
          label="Buscar por código, nombre o descripción..." single-line hide-details outlined dense rounded
          class="max-width-400" bg-color="grey lighten-5"></v-text-field>

        <v-spacer></v-spacer>

        <!-- Count Badge -->
        <v-chip color="primary lighten-5" text-color="primary" class="font-weight-black px-4" label>
          <v-icon left small>mdi-counter</v-icon>
          {{ getFilteredItemsCount() }} CUENTAS REGISTRADAS
        </v-chip>
      </v-toolbar>
    </v-card>

    <!-- Main Content -->
    <v-row>
      <v-col cols="12">
        <!-- Vista de Árbol -->
        <v-card v-if="viewMode === 'tree'" class="rounded-xl border-soft shadow-sm overflow-hidden" elevation="0">
          <div class="pa-0">
            <v-treeview v-model="selected" :items="filteredTreeItems" :search="search" :filter="customFilter"
              item-key="id" activatable open-on-click transition class="custom-tree pa-4">
              <!-- Slot prepend: Avatar with Icon -->
              <template v-slot:prepend="{ item }">
                <v-avatar size="36" :color="getAccountTypeColor(item.account_type_id) + ' lighten-5'" class="mr-2">
                  <v-icon :color="getAccountTypeColor(item.account_type_id)" size="18">
                    {{ getAccountTypeIcon(item.account_type_id) }}
                  </v-icon>
                </v-avatar>
              </template>

              <!-- Slot label: Account Info -->
              <template v-slot:label="{ item }">
                <div class="d-flex align-center py-2 flex-grow-1">
                  <div class="account-code-badge mr-4">
                    <span class="font-weight-black grey--text text--darken-4 letter-spacing-1">{{ item.code }}</span>
                  </div>

                  <v-divider vertical class="mx-4 my-2 opacity-10"></v-divider>

                  <div class="flex-grow-1 overflow-hidden">
                    <div class="d-flex align-center">
                      <span
                        :class="['text-subtitle-1 font-weight-bold', item.status ? 'grey--text text--darken-3' : 'grey--text opacity-50']">
                        {{ item.name }}
                      </span>
                      <v-chip v-if="item.is_system_only" x-small color="blue lighten-5" text-color="blue darken-2"
                        class="ml-2 font-weight-black" label>
                        <v-icon left x-small>mdi-shield-check</v-icon> CONTROL
                      </v-chip>
                      <v-chip v-if="item.is_category_required" x-small color="purple lighten-5"
                        text-color="purple darken-2" class="ml-2 font-weight-black" label>
                        <v-icon left x-small>mdi-tag-multiple</v-icon> CATEGORÍA
                      </v-chip>
                    </div>
                    <div v-if="item.note" class="text-caption grey--text text-truncate italic mt-n1">{{ item.note }}
                    </div>
                  </div>

                  <v-spacer></v-spacer>

                  <v-chip v-if="!item.status" x-small color="warning lighten-5" text-color="warning darken-2"
                    class="mr-4 font-weight-black" label>
                    INACTIVO
                  </v-chip>
                </div>
              </template>

              <!-- Slot append: Actions -->
              <template v-slot:append="{ item }">
                <div class="d-flex align-center gap-1 action-group">
                  <v-tooltip bottom>
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="primary" v-bind="attrs" v-on="on" @click.stop="ViewAccount(item)"
                        class="bg-blue-soft rounded-lg">
                        <v-icon small>mdi-eye</v-icon>
                      </v-btn>
                    </template>
                    <span>Ver movimientos</span>
                  </v-tooltip>

                  <v-tooltip bottom v-if="$store.state.auth.globalFeatures.includes('CON-013')">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="warning" v-bind="attrs" v-on="on" @click.stop="editItem(item)"
                        class="bg-orange-soft rounded-lg">
                        <v-icon small>mdi-pencil</v-icon>
                      </v-btn>
                    </template>
                    <span>Editar</span>
                  </v-tooltip>

                  <v-tooltip bottom v-if="$store.state.auth.globalFeatures.includes('CON-012')">
                    <template v-slot:activator="{ on, attrs }">
                      <v-btn icon small color="success" v-bind="attrs" v-on="on" @click.stop="addAccount(item)"
                        class="bg-green-soft rounded-lg">
                        <v-icon small>mdi-plus-circle</v-icon>
                      </v-btn>
                    </template>
                    <span>Sub-cuenta</span>
                  </v-tooltip>
                </div>
              </template>
            </v-treeview>
          </div>
        </v-card>

        <!-- Vista de Tabla -->
        <v-card v-else class="rounded-xl border-soft shadow-sm overflow-hidden" elevation="0">
          <v-data-table :headers="tableHeaders" :items="flattenedFilteredItems" :search="search" class="custom-table"
            :footer-props="{ 'items-per-page-text': 'Filas por página' }" :items-per-page="50">
            <template #item.code="{ item }">
              <div class="d-flex align-center">
                <div :style="`margin-left: ${item.level * 20}px`" class="tree-line" v-if="item.level > 0"></div>
                <span class="font-weight-black grey--text text--darken-4 letter-spacing-1">{{ item.code }}</span>
              </div>
            </template>

            <template #item.name="{ item }">
              <div class="d-flex align-center py-1">
                <v-avatar size="28" :color="getAccountTypeColor(item.account_type_id) + ' lighten-5'" class="mr-3">
                  <v-icon :color="getAccountTypeColor(item.account_type_id)" size="14">
                    {{ getAccountTypeIcon(item.account_type_id) }}
                  </v-icon>
                </v-avatar>
                <div class="d-flex flex-column">
                  <span class="text-body-2 font-weight-bold grey--text text--darken-3">{{ item.name }}</span>
                  <span v-if="item.is_system_only" class="text-tiny blue--text font-weight-bold">CUENTA CONTROL</span>
                  <span v-if="item.is_category_required" class="text-tiny purple--text font-weight-bold">CATEGORÍA OBLIGATORIA</span>
                </div>
              </div>
            </template>

            <template #item.status="{ item }">
              <v-chip :color="item.status ? 'success lighten-5' : 'warning lighten-5'"
                :text-color="item.status ? 'success darken-2' : 'warning darken-2'" x-small label
                class="font-weight-black">
                {{ item.status ? 'ACTIVO' : 'INACTIVO' }}
              </v-chip>
            </template>

            <template #item.actions="{ item }">
              <div class="d-flex align-center justify-end gap-1">
                <v-btn icon small color="primary" @click="ViewAccount(item)" class="bg-blue-soft rounded-lg">
                  <v-icon small>mdi-eye-outline</v-icon>
                </v-btn>
                <v-btn v-if="$store.state.auth.globalFeatures.includes('CON-013')" icon small color="warning"
                  @click="editItem(item)" class="bg-orange-soft rounded-lg">
                  <v-icon small>mdi-pencil-outline</v-icon>
                </v-btn>
                <v-btn v-if="$store.state.auth.globalFeatures.includes('CON-012')" icon small color="success"
                  @click="addAccount(item)" class="bg-green-soft rounded-lg">
                  <v-icon small>mdi-plus-circle-outline</v-icon>
                </v-btn>
              </div>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Dialog para Crear/Editar Cuenta -->
    <v-dialog v-model="dialog" max-width="650px" persistent overlay-color="grey darken-4">
      <v-card class="rounded-xl overflow-hidden shadow-xl">
        <v-toolbar flat color="primary" dark class="px-2">
          <v-icon left>mdi-bank-plus</v-icon>
          <v-toolbar-title class="font-weight-black letter-spacing-1">{{ formTitle.toUpperCase() }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="close" class="bg-white-opacity-10">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pa-8 pb-4">
          <v-form ref="form" v-model="formValid" lazy-validation>
            <!-- Account Parent Context -->
            <v-alert v-if="seletecName !== ''" text color="primary" icon="mdi-account-arrow-right" rounded="lg"
              class="mb-6 font-weight-medium bg-blue-soft">
              Creando sub-cuenta bajo: <strong class="ml-1">{{ seletecName }}</strong>
            </v-alert>

            <v-row>
              <v-col cols="12" md="8">
                <div class="text-caption font-weight-black primary--text mb-2 text-uppercase">Nombre de la Cuenta</div>
                <v-text-field v-model="editedItem.name" placeholder="Ej: Caja General o Gastos de Venta" outlined dense
                  required :rules="nameRules" bg-color="white"></v-text-field>
              </v-col>
              <v-col cols="12" md="4">
                <div class="text-caption font-weight-black primary--text mb-2 text-uppercase">Código</div>
                <v-text-field v-model="editedItem.code" placeholder="Ej: 1111-01" outlined dense required
                  :rules="codeRules" bg-color="white"></v-text-field>
              </v-col>

              <v-col cols="12">
                <div class="text-caption font-weight-black primary--text mb-2 text-uppercase">Categoría Fiscal (DGII)
                </div>
                <v-autocomplete v-model="selectedCategoryDgii" :items="categoryDgii" outlined dense required
                  return-object :rules="dgiiRules" placeholder="Seleccione categoría para reporte 606" item-text="text"
                  item-value="value" bg-color="white">
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title class="text-body-1 font-weight-bold">{{ item.text }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col cols="12">
                <div class="text-caption font-weight-black primary--text mb-2 text-uppercase">Descripción / Nota</div>
                <v-textarea v-model="editedItem.note"
                  placeholder="Agregue una nota informativa sobre el uso de esta cuenta..." outlined dense rows="2"
                  bg-color="white" no-resize></v-textarea>
              </v-col>

              <v-col cols="12" sm="6">
                <v-card outlined class="pa-4 bg-grey-soft border-soft rounded-lg h-full">
                  <div class="d-flex align-center">
                    <v-icon color="primary" class="mr-3">mdi-shield-account</v-icon>
                    <div>
                      <div class="text-caption font-weight-bold grey--text">Permisos</div>
                      <v-checkbox v-model="editedItem.is_system_only" label="Cuenta de Solo Sistema" color="primary"
                        dense hide-details class="mt-0 font-weight-medium"></v-checkbox>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12" sm="6">
                <v-card outlined class="pa-4 bg-grey-soft border-soft rounded-lg h-full">
                  <div class="d-flex align-center">
                    <v-icon :color="editedItem.status ? 'success' : 'warning'" class="mr-3">{{ editedItem.status ?
                      'mdi-check-decagram' : 'mdi-pause-circle' }}</v-icon>
                    <div>
                      <div class="text-caption font-weight-bold grey--text">Estado</div>
                      <v-switch v-model="editedItem.status" :label="editedItem.status ? 'ACTIVA' : 'INACTIVA'"
                        :color="editedItem.status ? 'success' : 'warning'" dense hide-details
                        class="mt-0 font-weight-black"></v-switch>
                    </div>
                  </div>
                </v-card>
              </v-col>

              <v-col cols="12">
                <v-card outlined class="pa-4 bg-grey-soft border-soft rounded-lg">
                  <div class="d-flex align-center">
                    <v-icon color="purple" class="mr-3">mdi-tag-multiple</v-icon>
                    <div>
                      <div class="text-caption font-weight-bold grey--text">Categorías contables</div>
                      <v-switch v-model="editedItem.is_category_required" label="Categoría obligatoria"
                        color="purple" dense hide-details class="mt-0 font-weight-medium"
                        @change="onCategoryRequiredChange"></v-switch>
                    </div>
                  </div>

                  <template v-if="editedItem.is_category_required">
                    <v-autocomplete v-model="editedItem.allowed_category_ids" :items="rootCategories"
                      item-text="name" item-value="id" label="Categorías permitidas" multiple chips small-chips
                      deletable-chips outlined dense class="mt-4" :rules="allowedCategoryRules"
                      @change="pruneAllowedSubcategories"></v-autocomplete>

                    <v-autocomplete v-model="editedItem.allowed_subcategory_ids" :items="availableSubcategories"
                      item-text="name" item-value="id" label="Subcategorías permitidas" multiple chips small-chips
                      deletable-chips outlined dense persistent-hint
                      hint="Si no selecciona subcategorías de una categoría, se permiten todas las de esa categoría"
                      :disabled="!editedItem.allowed_category_ids || editedItem.allowed_category_ids.length === 0"></v-autocomplete>
                  </template>

                  <v-checkbox v-if="editedIndex > -1 && childAccountsCount > 0"
                    v-model="editedItem.apply_category_to_children" color="purple" dense hide-details class="mt-4"
                    :label="`Aplicar esta configuración a sus ${childAccountsCount} sub-cuentas`"></v-checkbox>
                </v-card>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="pa-6 bg-grey-soft">
          <v-spacer></v-spacer>
          <v-btn text @click="close" class="rounded-xl px-6 font-weight-bold">Cancelar</v-btn>
          <v-btn color="primary" depressed @click="save" :loading="saving" :disabled="!formValid"
            class="rounded-xl px-8 font-weight-black shadow-sm">
            <v-icon left>mdi-content-save</v-icon> {{ editedIndex === -1 ? "Crear Cuenta" : "Guardar Cambios" }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import jsPDF from "jspdf";
import ExcelJS from "exceljs";

export default {
  data: () => ({
    // Control de vistas
    viewMode: "tree",
    search: "",
    statusFilter: null,
    selected: [],
    saving: false,
    formValid: false,

    // Opciones para filtros
    statusOptions: [
      { text: "Activas", value: true },
      { text: "Inactivas", value: false },
    ],

    // Headers para la tabla
    tableHeaders: [
      {
        text: "Código",
        value: "code",
        width: "140px",
        sortable: true,
        class: "font-weight-bold",
      },
      {
        text: "Nombre de Cuenta",
        value: "name",
        sortable: true,
      },
      {
        text: "Estado",
        value: "status",
        width: "120px",
        sortable: true,
        align: "center",
      },
      {
        text: "Acciones",
        value: "actions",
        width: "140px",
        sortable: false,
        align: "center",
      },
    ],

    // Reglas de validación
    nameRules: [
      (v) => !!v || "El nombre de la cuenta es requerido",
      (v) => (v && v.length <= 100) || "El nombre debe tener menos de 100 caracteres",
    ],
    codeRules: [
      (v) => !!v || "El código de la cuenta es requerido",
      (v) => (v && v.length <= 20) || "El código debe tener menos de 20 caracteres",
    ],
    dgiiRules: [(v) => !!v || "La categoría DGII es requerida"],
    allowedCategoryRules: [(v) => (v && v.length > 0) || "Seleccione al menos una categoría"],
    diaryCategories: [],

    // Datos del componente original
    selectedCategoryDgii: null,
    categoryDgii: [],
    seletecName: "",
    dialog: false,
    items: [],
    accounts: [],
    can_edit: false,
    itemsBussines: [],
    itemsLocations: [],
    itemsLocations2: [],
    itemsDepartment: [],
    itemSaved: {},
    newItem: [],
    itemsRoutes: [],
    editedItem: {},
    catalogue_id: null,
    editedIndex: -1,
    filters: { business: "", location: null, department_id: "", routes: "" },
  }),

  created() {
    this.getData();
  },

  computed: {
    rootCategories() {
      return this.diaryCategories.filter((c) => c.parent_id === null && c.id !== 0);
    },

    // Subcategorías de las categorías seleccionadas, agrupadas por categoría
    availableSubcategories() {
      const selected = this.editedItem.allowed_category_ids || [];
      const result = [];
      this.rootCategories
        .filter((root) => selected.includes(root.id))
        .forEach((root) => {
          const children = this.diaryCategories.filter((c) => c.parent_id === root.id);
          if (children.length === 0) return;
          result.push({ header: root.name });
          children.forEach((c) => result.push({ id: c.id, name: c.name }));
        });
      return result;
    },

    // Cantidad de sub-cuentas (todos los niveles) de la cuenta en edición
    childAccountsCount() {
      if (!this.editedItem.id) return 0;
      let count = 0;
      const pending = [this.editedItem.id];
      while (pending.length > 0) {
        const parentId = pending.pop();
        this.accounts.forEach((acc) => {
          if (acc.parent_id === parentId) {
            count++;
            pending.push(acc.id);
          }
        });
      }
      return count;
    },

    formTitle() {
      return this.editedIndex === -1
        ? "Crear Nueva Cuenta Contable"
        : "Editar Cuenta Contable";
    },

    // Filtros para el TreeView
    filteredTreeItems() {
      // Trabajar con una copia fresca de los datos originales
      return this.filterTreeItems(JSON.parse(JSON.stringify(this.items)));
    },

    // Items planos filtrados para la tabla
    flattenedFilteredItems() {
      const flattened = this.flattenTree(this.filteredTreeItems);
      return this.applyTableFilters(flattened);
    },
  },

  methods: {
    // Filtro personalizado para el TreeView
    customFilter(item, search, textKey) {
      if (!search) return true;

      const searchLower = search.toLowerCase();
      return (
        item.name.toLowerCase().includes(searchLower) ||
        item.code.toLowerCase().includes(searchLower) ||
        (item.note && item.note.toLowerCase().includes(searchLower))
      );
    },

    // Filtrar elementos del árbol recursivamente
    // Filtrar elementos del árbol recursivamente - VERSIÓN CORREGIDA
    filterTreeItems(items) {
      // Si no hay filtros, devolver los items originales sin modificar
      if (!this.search && this.statusFilter === null) {
        return items;
      }

      // Función de filtrado recursivo que no modifica el original
      const filterRecursive = (nodes) => {
        return nodes.reduce((filtered, node) => {
          // Crear una copia del nodo actual
          const nodeCopy = { ...node };

          let includeNode = false;

          // Verificar si el nodo actual coincide con los filtros
          if (this.search) {
            const searchLower = this.search.toLowerCase();
            const nodeMatches =
              node.name.toLowerCase().includes(searchLower) ||
              node.code.toLowerCase().includes(searchLower) ||
              (node.note && node.note.toLowerCase().includes(searchLower));

            includeNode = includeNode || nodeMatches;
          } else {
            includeNode = true; // Si no hay búsqueda, incluir por defecto
          }

          // Filtro por estado
          if (this.statusFilter !== null) {
            includeNode = includeNode && node.status === this.statusFilter;
          }

          // Procesar hijos si existen
          if (node.children && node.children.length > 0) {
            const filteredChildren = filterRecursive(node.children);
            nodeCopy.children = filteredChildren;

            // Si tiene hijos filtrados, incluir el nodo padre
            if (filteredChildren.length > 0) {
              includeNode = true;
            }
          } else {
            nodeCopy.children = [];
          }

          if (includeNode) {
            filtered.push(nodeCopy);
          }

          return filtered;
        }, []);
      };

      return filterRecursive(items);
    },

    // Verificar si tiene hijos que coinciden con la búsqueda
    hasMatchingChildren(item, searchLower) {
      if (!item.children) return false;

      return item.children.some(
        (child) =>
          child.name.toLowerCase().includes(searchLower) ||
          child.code.toLowerCase().includes(searchLower) ||
          (child.note && child.note.toLowerCase().includes(searchLower)) ||
          this.hasMatchingChildren(child, searchLower)
      );
    },

    // Verificar si tiene hijos con el estado especificado
    hasChildrenWithStatus(item, status) {
      if (!item.children) return false;

      return item.children.some(
        (child) => child.status === status || this.hasChildrenWithStatus(child, status)
      );
    },

    // Aplicar filtros adicionales a la tabla
    applyTableFilters(items) {
      if (this.statusFilter !== null) {
        return items.filter((item) => item.status === this.statusFilter);
      }
      return items;
    },

    // Contar elementos filtrados
    getFilteredItemsCount() {
      return this.flattenTree(this.filteredTreeItems).length;
    },

    // Obtener icono por tipo de cuenta - mejorado
    getAccountTypeIcon(typeId) {
      const icons = {
        1: "mdi-cash-multiple", // Activos - Efectivo múltiple ✅
        2: "mdi-bank", // Pasivos - Banco ✅
        3: "mdi-trending-up", // Patrimonio - Tendencia arriba ✅
        4: "mdi-chart-line", // Ingresos - Gráfico línea ✅
        5: "mdi-trending-down", // Costos - Tendencia abajo ✅
        6: "mdi-receipt", // Gastos - Recibo ✅
        7: "mdi-plus-circle", // Otros Ingresos - Círculo plus ✅
        8: "mdi-account-minus", // Otros Egresos - Cuenta minus ✅
        9: "mdi-balance",
        default: "mdi-file-outline", // Por defecto - Archivo outline ✅
      };
      return icons[typeId] || icons.default;
    },

    // Obtener color por tipo de cuenta - mejorado
    getAccountTypeColor(typeId) {
      const colors = {
        1: "teal", // Activos - Verde azulado
        2: "amber darken-1", // Pasivos - Amarillo/naranja
        3: "orange darken-1", // Patrimonio - Naranja
        4: "red", // Ingresos - Rojo
        5: "purple", // Costos - Morado
        6: "grey darken-1", // Gastos - Gris
        7: "red lighten-1", // Otros Ingresos - Rojo claro
        8: "grey darken-2", // Otros Egresos - Gris oscuro
        9: "blue-grey", // Balances Iniciales - Gris azul
        default: "grey",
      };
      return colors[typeId] || colors.default;
    },

    // Obtener datos del servidor
    async getData() {
      try {
        // Cargar categorías DGII
        let resultCategoryDgii = await this.api_Get("accounting/category_dgii");
        const dataCategoryDgii = await resultCategoryDgii.json();

        this.categoryDgii = dataCategoryDgii.map((item) => ({
          text: item.name,
          value: item.id,
        }));

        // Cargar categorías contables
        const resultCategories = await this.api_Get("accounting/categories_diary_book");
        this.diaryCategories = await resultCategories.json();

        // Cargar cuentas
        let resultTypeAccounts = await this.api_Get("accounting/accounts");
        const dataTypeAccounts = await resultTypeAccounts.json();
        this.accounts = dataTypeAccounts;

        // Procesar datos para el árbol
        let child = dataTypeAccounts.map((items) => ({
          id: items.id,
          name: items.name,
          parent: items.parent_id,
          code: items.code,
          note: ["null", "undefined"].includes(items.note) ? "" : items.note,
          account_type_id: items.account_type_id,
          dgii_category_id: items.dgii_category_id,
          status: items.status,
          is_system_only: items.is_system_only || false,
          is_category_required: items.is_category_required || false,
          allowed_category_ids: items.allowed_category_ids || [],
          allowed_subcategory_ids: items.allowed_subcategory_ids || [],
        }));

        // Ordenar por código
        child.sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

        // Crear estructura de árbol
        const nest = (items, id = null, link = "parent") =>
          items
            .filter((item) => item[link] === id)
            .map((item) => ({ ...item, children: nest(items, item.id) }));

        this.items = nest(child);
      } catch (error) {
        this.$toast.error("Error al cargar los datos");
        console.error(error);
      }
    },

    // Aplanar árbol para tabla
    flattenTree(data, level = 0, result = []) {
      data.forEach((node) => {
        result.push({
          ...node,
          level: level,
        });
        if (node.children && node.children.length > 0) {
          this.flattenTree(node.children, level + 1, result);
        }
      });
      return result;
    },

    // Generar contenido del PDF
    generatePDFContent(doc, data) {
      let y = 20;
      const lineHeight = 8;
      const pageHeight = doc.internal.pageSize.height;
      const pageWidth = doc.internal.pageSize.width;

      // Título
      doc.setFontSize(16);
      doc.text("Catálogo de Cuentas Contables", pageWidth / 2, y, { align: "center" });
      y += lineHeight * 2;

      // Contenido
      doc.setFontSize(10);
      data.forEach((row) => {
        if (y > pageHeight - 20) {
          doc.addPage();
          y = 20;
        }

        const indent = "  ".repeat(row.level);
        const text = `${indent}${row.code} - ${row.name}`;
        doc.text(text, 10, y);
        y += lineHeight;
      });
    },

    // Descargar PDF
    downloadPDF() {
      const flattenedData = this.flattenTree(this.items);
      const doc = new jsPDF();
      this.generatePDFContent(doc, flattenedData);
      doc.save("Catalogo_de_Cuentas.pdf");
    },

    // Descargar Excel
    async downloadXLSX() {
      const flattenedData = this.flattenTree(this.items);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Catálogo de Cuentas");

      // Headers
      worksheet.addRow(["Código", "Nombre de Cuenta", "Estado"]);
      worksheet.columns = [
        { header: "Código", key: "code", width: 15 },
        { header: "Nombre de Cuenta", key: "name", width: 60 },
        { header: "Estado", key: "status", width: 12 },
      ];

      // Datos con indentación
      flattenedData.forEach((row) => {
        const indent = "  ".repeat(row.level);
        worksheet.addRow({
          code: row.code,
          name: indent + row.name,
          status: row.status ? "Activo" : "Inactivo",
        });
      });

      // Configurar niveles de grupo
      flattenedData.forEach((row, index) => {
        const currentRow = worksheet.getRow(index + 2);
        currentRow.outlineLevel = row.level;
      });

      // Descargar archivo
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/octet-stream" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "Catalogo_de_Cuentas.xlsx";
      link.click();
    },

    // Guardar cuenta (crear o editar)
    async save() {
      // Validar formulario
      if (!this.$refs.form.validate()) {
        this.$toast.warning("Por favor, completa todos los campos requeridos");
        return;
      }

      this.saving = true;

      try {
        const payload = {
          chart_of_account_id: this.editedIndex > -1 ? this.editedItem.id : null,
          account_type_id:
            this.editedIndex > -1
              ? this.editedItem.account_type_id
              : this.itemSaved.account_type_id,
          parent_id: this.editedIndex > -1 ? this.editedItem.parent : this.itemSaved.id,
          dgii_category_id: this.selectedCategoryDgii.value,
          status: this.editedItem.status,
          code: this.editedItem.code,
          name: this.editedItem.name,
          note: this.editedItem.note || "",
          is_system_only: this.editedItem.is_system_only || false,
          is_category_required: this.editedItem.is_category_required || false,
          allowed_category_ids: this.editedItem.allowed_category_ids || [],
          allowed_subcategory_ids: this.editedItem.allowed_subcategory_ids || [],
          apply_category_to_children: this.editedItem.apply_category_to_children === true,
        };

        const responseRequest = await this.api_Post(
          "accounting/accounting_account",
          payload
        );

        if (responseRequest.status === 200) {
          await this.getData();
          this.$toast.success(
            this.editedIndex > -1
              ? "Cuenta actualizada exitosamente"
              : "Cuenta creada exitosamente"
          );
          this.close();
        } else {
          throw new Error("Error en la respuesta del servidor");
        }
      } catch (error) {
        this.$toast.error(
          this.editedIndex > -1
            ? "Error al actualizar la cuenta"
            : "Error al crear la cuenta"
        );
        console.error(error);
      } finally {
        this.saving = false;
      }
    },

    onCategoryRequiredChange(value) {
      if (!value) {
        this.editedItem.allowed_category_ids = [];
        this.editedItem.allowed_subcategory_ids = [];
      }
    },

    // Quita subcategorías cuya categoría ya no está seleccionada
    pruneAllowedSubcategories() {
      const selected = this.editedItem.allowed_category_ids || [];
      this.editedItem.allowed_subcategory_ids = (this.editedItem.allowed_subcategory_ids || []).filter((id) => {
        const sub = this.diaryCategories.find((c) => c.id === id);
        return sub && selected.includes(sub.parent_id);
      });
    },

    // Ver detalles de cuenta
    ViewAccount(item) {
      this.$router.push({
        name: "ViewEntryDiaryBook",
        params: { id: item.id },
      });
    },

    // Editar cuenta existente
    editItem(item) {
      this.editedIndex = 0;
      this.editedItem = Object.assign({}, item, {
        allowed_category_ids: [...(item.allowed_category_ids || [])],
        allowed_subcategory_ids: [...(item.allowed_subcategory_ids || [])],
        apply_category_to_children: false,
      });

      // Buscar categoría DGII seleccionada
      this.selectedCategoryDgii =
        this.categoryDgii.find((cat) => cat.value === item.dgii_category_id) || null;

      // Buscar cuenta padre
      const parentAccount = this.accounts.find((acc) => acc.id === item.parent);
      this.seletecName = parentAccount
        ? `${parentAccount.code} - ${parentAccount.name}`
        : "";

      this.dialog = true;
    },

    // Cerrar dialog
    close() {
      this.dialog = false;
      this.editedItem = {
        status: true,
        is_system_only: false,
        is_category_required: false,
        allowed_category_ids: [],
        allowed_subcategory_ids: [],
      };
      this.selectedCategoryDgii = null;
      this.seletecName = "";
      this.editedIndex = -1;
      this.itemSaved = {};

      // Reset form validation
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },

    // Agregar subcuenta
    addAccount(item) {
      this.editedItem = {
        status: true,
        name: "",
        code: "",
        note: "",
        is_system_only: false,
        is_category_required: false,
        allowed_category_ids: [],
        allowed_subcategory_ids: [],
      };
      this.itemSaved = Object.assign({}, item);
      this.selectedCategoryDgii = null;
      this.editedIndex = -1;
      this.seletecName = `${item.code} - ${item.name}`;
      this.dialog = true;
    },

    // Agregar cuenta raíz
    addRootAccount() {
      this.editedItem = {
        status: true,
        name: "",
        code: "",
        note: "",
        is_system_only: false,
        is_category_required: false,
        allowed_category_ids: [],
        allowed_subcategory_ids: [],
      };
      this.itemSaved = {};
      this.selectedCategoryDgii = null;
      this.editedIndex = -1;
      this.seletecName = "";
      this.dialog = true;
    },
  },
};
</script>

<style scoped>
/* Luxury Utility Classes */
.bg-light-soft {
  background-color: #f8fafd !important;
}

.bg-grey-soft {
  background-color: #f1f3f6 !important;
}

.bg-blue-soft {
  background-color: rgba(25, 118, 210, 0.05) !important;
}

.bg-orange-soft {
  background-color: rgba(255, 152, 0, 0.05) !important;
}

.bg-green-soft {
  background-color: rgba(76, 175, 80, 0.05) !important;
}

.bg-white-opacity-10 {
  background-color: rgba(255, 255, 255, 0.1) !important;
}

.border-soft {
  border: 1px solid rgba(0, 0, 0, 0.05) !important;
}

.shadow-sm {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.03) !important;
}

.shadow-xs {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05) !important;
}

.shadow-xl {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
}

.letter-spacing-1 {
  letter-spacing: 1px !important;
}

.text-tiny {
  font-size: 0.65rem !important;
}

.italic {
  font-style: italic;
}

.gap-1 {
  gap: 4px;
}

.gap-2 {
  gap: 8px;
}

/* TreeView Customization */
.custom-tree {
  font-family: inherit;
}

.custom-tree>>>.v-treeview-node__root {
  min-height: 56px;
  padding: 0 16px;
  border-radius: 12px;
  margin-bottom: 4px;
  transition: all 0.2s ease;
}

.custom-tree>>>.v-treeview-node__root:hover {
  background-color: white !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  transform: translateX(4px);
}

.custom-tree>>>.v-treeview-node__level {
  width: 24px;
}

.custom-tree>>>.v-treeview-node__toggle {
  color: #1976d2 !important;
}

.custom-tree>>>.v-treeview-node__content {
  margin-left: 0;
}

/* Hide actions until hover */
.action-group {
  opacity: 0;
  transition: opacity 0.2s ease;
}

.custom-tree>>>.v-treeview-node__root:hover .action-group {
  opacity: 1;
}

/* Badge for Account Code */
.account-code-badge {
  min-width: 80px;
  display: flex;
  align-items: center;
}

/* Custom Table Design */
.custom-table {
  background: transparent !important;
}

.custom-table>>>th {
  text-transform: uppercase;
  font-size: 0.75rem !important;
  font-weight: 900 !important;
  color: #78909c !important;
  letter-spacing: 1px;
  background-color: #f8fafd !important;
  height: 56px !important;
  border-bottom: 2px solid #eceff1 !important;
}

.custom-table>>>td {
  height: 64px !important;
  border-bottom: 1px solid rgba(0, 0, 0, 0.03) !important;
}

.custom-table>>>tr:hover {
  background-color: rgba(25, 118, 210, 0.02) !important;
}

/* Tree Lines for Table */
.tree-line {
  height: 32px;
  width: 2px;
  background: #e0e0e0;
  margin-right: 12px;
  position: relative;
}

.tree-line::after {
  content: '';
  position: absolute;
  top: 16px;
  left: 0;
  width: 10px;
  height: 2px;
  background: #e0e0e0;
}

/* Max Widths */
.max-width-400 {
  max-width: 400px;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .custom-tree>>>.v-treeview-node__root {
    padding: 0 8px;
  }

  .account-code-badge {
    min-width: 60px;
  }
}

/* Transitions */
.v-btn,
.v-card,
.v-chip {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.hover-bg-light:hover {
  background-color: #f8fafd !important;
}
</style>
